<template>
  <div
    class="refonte-input"
    v-bind:class="`${props.error ? 'input-error' : ''} ${props.class}`">
    <input
      v-model="inputValue"
      v-on:input="updateValue"
      v-bind:type="props.type"
      v-bind:placeholder="props.placeholder"
      v-bind:disabled="props.disabled"
      v-bind:maxlength="props.maxlength"
      v-on:keydown="(e) => restrictByFormat(e, format)"
      v-bind:class="props.input_class" />
    <label v-bind:class="{ 'input-with-value': inputValue !== '' }">
      {{ props.placeholder }}<span v-if="props.required"> *</span>
    </label>
    <div
      v-if="props.error && props.error_message"
      class="input-error-message">
      {{ props.error_message }}
    </div>
    <div
      class="mt-2 flex px-1"
      v-if="props.description">
      <span class="text-green-600"><IconShield /></span>
      <p class="text-gray-500 text-[11px] italic leading-snug">
        {{ props.description }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import IconShield from '@/assets/img/refonte/icons/information/icon-shield.svg?component';
  const props = withDefaults(
    defineProps<{
      type?: string;
      value?: string;
      placeholder: string;
      required?: boolean;
      disabled?: boolean;
      error?: boolean;
      error_message?: string;
      maxlength?: number;
      format?: string;
      class?: string;
      input_class?: string;
      description?: string;
    }>(),
    {
      type: 'text',
      value: '',
      required: false,
      disabled: false,
      error: false,
      class: '',
      input_class: ''
    }
  );

  const inputValue = ref<string>(props.value);

  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (!newValue) {
        inputValue.value = oldValue;
      } else {
        inputValue.value = newValue;
      }
    }
  );

  const emit = defineEmits(['input']);

  function updateValue() {
    emit('input', inputValue.value);
  }

  const restrictByFormat = (
    $event: KeyboardEvent,
    format: undefined | string
  ) => {
    if (format === 'numeric') {
      return restrictKeyPress($event, NUMERIC_ALLOWED_KEYS);
    } else if (format === 'phone') {
      return restrictKeyPress($event, PHONE_ALLOWED_KEYS);
    }
  };
  const NUMBER_ALLOWED_KEYS = Array.from({ length: 10 }, (i, v) =>
    v.toString()
  );
  const NUMERIC_ALLOWED_KEYS = [...NUMBER_ALLOWED_KEYS, '.', ' '];

  const PHONE_ALLOWED_KEYS = [...NUMBER_ALLOWED_KEYS, '+', ' '];

  const restrictKeyPress = ($event: KeyboardEvent, allowedKeys: string[]) => {
    const { key } = $event;
    if (!allowedKeys.includes(key)) {
      $event.preventDefault();
    }
  };
</script>

<style lang="scss" scoped>
  .refonte-input {
    @apply relative;
    padding-bottom: 16px;

    input {
      @apply flex flex-grow items-center self-stretch border border-refonte_blue-100 bg-refonte-white text-refonte-black;
      padding: 17px 7px 4px;
      border-radius: 4px;
      gap: 6px;
      height: 44px;
      font-size: 13px;
      font-weight: 400;
      line-height: 16px;

      &::placeholder {
        @apply text-refonte-white;
      }

      &:focus-visible {
        outline: none;
      }

      &:hover {
        @apply border-refonte_blue-600;
      }

      &:focus {
        @apply border-refonte_blue-800;
      }

      &[disabled] {
        @apply cursor-not-allowed border-refonte_blue-200 text-refonte_blue-200;
      }
    }

    label {
      @apply absolute text-refonte_blue-300;
      left: 8px;
      top: 14px;
      transform: translateY(0);
      transition: all 0.2s ease-in-out;
      pointer-events: none;
      font-size: 13px;
      font-weight: 400;
      line-height: 16px;
    }

    label.input-with-value,
    input:focus + label {
      transform: translateY(-9px);
      font-size: 10px;
      font-weight: 400;
      line-height: 13px;
    }

    &.input-error {
      input {
        @apply border-jinius-red text-jinius-red #{!important};
      }

      label {
        @apply text-jinius-red #{!important};
      }
    }

    .input-error-message {
      @apply text-jinius-red;
      bottom: 2px;
      font-size: 10px;
      font-weight: 400;
      line-height: 13px;
    }
  }
</style>
